import {Component, Input, OnInit, PLATFORM_ID, TemplateRef, ViewEncapsulation, inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {AdvFormService} from "../services/adv-form.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {ApiUrl} from "../../../shared/models/apiurl.models";
import {adsRoute} from "../../../app.models";
import {finalize, Observable} from "rxjs";
import {LoadingMaskServiceService} from "../../../components/loading-frame/loading-frame.component";
import {MatDialog} from "@angular/material/dialog";
import {CreditCardComponent} from "./credit-card/credit-card.component";
import {TermsConsentDialogComponent} from "../../../components/person-contact/terms-consent-dialog/terms-consent-dialog.component";
import {ConsentService} from "../../../services/consent.service";
import {CouponService} from "../../../services/coupon.service";
import {TokenStore} from "../../../services/token-store";
import {EligibleCouponRedemption} from "../../../models/coupon";


export enum RouteType{
  LEVEL1="LEVEL1",
  LEVEL2="LEVEL2",
  LEVEL3="LEVEL3"
}

@Component({
    selector: 'app-select-route',
    templateUrl: './select-route.component.html',
    styleUrls: ['./select-route.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SelectRouteComponent implements OnInit {
  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiUrl.getJWTToken()
    })
  }
  closeResult = '';
  @Input()
  greenPass: boolean = false;

  selectedRoute!: RouteType;
  selectedLevel: string | null = null;
  isSubmitting = false;
  submitError = '';
  dynamicMessage = 'הגעת למכסת המודעות החינמיות. ניתן לבחור מסלול מקודם ולהמשיך לפרסם.';

  adsDescription(adsDescription: string | null | undefined): string[] {
    return [...new Set((adsDescription || '')
      .split(/\r?\n|\r/)
      .map(line => line.trim())
      .filter(Boolean))];
  }

  isRecommended(route: adsRoute): boolean {
    return route.level === RouteType.LEVEL2;
  }

  isBlocked(route: adsRoute): boolean {
    return Boolean((route as adsRoute & {blocked?: boolean}).blocked ?? route.block);
  }
  constructor(private modalService: MatDialog, public AdvFormService: AdvFormService, private http: HttpClient, private apiUrl:ApiUrl, private loadingMaskServiceService: LoadingMaskServiceService, public advFormService: AdvFormService, private consentService: ConsentService) { }

  /**
   * SCRUM-86 — the caller's publishing quota, or null while loading /
   * if the lookup failed.
   *
   * A failed lookup deliberately leaves this null, which renders the
   * existing paid-route picker. The server enforces the real limit on
   * create, so the worst case is one extra screen, never a free ad.
   */
  vipQuota: any = null;
  vipBusy = false;

  /**
   * SCRUM-126 — publishing is blocked until the תקנון is accepted in the
   * contact-details step. The server enforces the same rule.
   */
  get termsAccepted(): boolean {
    return this.advFormService.termsAcceptedLocally
      || !!this.advFormService.getQuestion('userContactDetails')?.acceptTerms;
  }

  readonly termsRequiredMessage = 'לא ניתן לפרסם מודעה לפני אישור התקנון.';

  /**
   * SCRUM-126 — "a clear message with a link to approve the terms": opens
   * the same read-to-the-end dialog as the contact step, and on approval
   * ticks the form's terms box and records the approval on the server
   * (so it shows in the personal area with its original date and time).
   */
  approveTermsHere(event?: Event): void {
    event?.preventDefault();
    this.modalService.open(TermsConsentDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100vh - 24px)',
      direction: 'rtl',
      autoFocus: false
    }).afterClosed().subscribe((accepted: boolean | undefined) => {
      if (!accepted) return;
      // Unlock the plans immediately, then save the approval in the
      // background so it is never asked for again (SCRUM-239).
      this.markTermsAccepted();
      this.submitError = '';
      // Logged-out users are recorded at publish time instead (createAdv).
      this.consentService.update({acceptTerms: true}).subscribe({error: () => {}});
    });
  }

  get isVip(): boolean {
    return !!this.vipQuota?.is_vip;
  }

  // SCRUM-195: read the field names GET client-api/subscriptions/quota/
  // actually returns (subscription/quota.py QuotaStatus.as_dict - the same
  // ones the Flutter app reads). This used to read can_publish / used /
  // limit / unlimited, which the server never sends, so every VIP - even a
  // brand-new one with 0 ads - saw "quota reached" and "undefined".
  get vipUnlimited(): boolean {
    const q = this.vipQuota;
    return !!q?.is_vip && (q.concurrent_ads_limit === null || q.concurrent_ads_limit === undefined);
  }

  get vipCanPublish(): boolean {
    const q = this.vipQuota;
    return !!q?.is_vip && q.quota_reached !== true;
  }

  get vipUsageLine(): string {
    const q = this.vipQuota;
    if (!q) return '';
    if (this.vipUnlimited) return 'מודעות ללא הגבלה';
    const used = Number(q.active_ads_count ?? 0);
    return used + ' מתוך ' + q.concurrent_ads_limit + ' מודעות פעילות';
  }

  // ── SCRUM-313 + 328 — publishing with a coupon ───────────────────────
  private readonly couponService = inject(CouponService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Usable coupons in server order (earliest expiry first). Empty = no coupon card at all. */
  eligibleCoupons: EligibleCouponRedemption[] = [];
  /** Picked explicitly by the user - never auto-selected. */
  selectedCouponId: number | null = null;
  couponBusy = false;
  couponError = '';
  /**
   * One publish attempt = one coupon_request_id (idempotency key). Re-sent
   * when the same attempt is retried after a network / server failure, so
   * the server never creates a second ad; dropped after a success or a
   * definitive refusal.
   */
  private couponAttempt: {redemptionId: number; requestId: string} | null = null;

  get hasCoupons(): boolean {
    return this.eligibleCoupons.length > 0;
  }

  get selectedCoupon(): EligibleCouponRedemption | null {
    return this.eligibleCoupons.find(c => c.redemption_id === this.selectedCouponId) ?? null;
  }

  /** Opening the page or picking a coupon consumes nothing - only the publish call does. */
  loadCoupons(): void {
    if (!this.isBrowser || !TokenStore.isLoggedIn()) {
      this.eligibleCoupons = [];
      return;
    }
    this.couponService.loadEligible().subscribe({
      next: list => {
        this.eligibleCoupons = list || [];
        if (this.selectedCouponId !== null && !this.selectedCoupon) this.selectedCouponId = null;
        // A coupon the user explicitly picked in the Adaline plan cards.
        const preferred = this.couponService.takePreferred();
        if (preferred !== null && this.selectedCouponId === null
            && this.eligibleCoupons.some(c => c.redemption_id === preferred)) {
          this.selectedCouponId = preferred;
        }
      },
      // A failed lookup = no coupons; the regular plans still work.
      error: () => { this.eligibleCoupons = []; this.selectedCouponId = null; },
    });
  }

  selectCoupon(coupon: EligibleCouponRedemption): void {
    if (this.isSubmitting) return;
    this.selectedCouponId = coupon.redemption_id;
    this.couponError = '';
  }

  /** "פרסם באמצעות הקופון" - publishes ONCE; never opens the payment step. */
  publishWithCoupon(freeAdv: TemplateRef<unknown>): void {
    if (this.isSubmitting || this.couponBusy) return;
    const coupon = this.selectedCoupon;
    if (!coupon) {
      this.couponError = 'יש לבחור קופון כדי להמשיך.';
      return;
    }
    if (!this.termsAccepted) {
      this.submitError = this.termsRequiredMessage;
      return;
    }
    if (!this.couponAttempt || this.couponAttempt.redemptionId !== coupon.redemption_id) {
      this.couponAttempt = {redemptionId: coupon.redemption_id, requestId: this.couponService.newRequestId()};
    }
    const attempt = this.couponAttempt;

    this.isSubmitting = true;
    this.couponBusy = true;
    this.couponError = '';
    this.submitError = '';
    this.selectedLevel = null;
    // The server uses the coupon's level; send it so the payload is valid.
    this.advFormService.addQuestion('adsRoute', coupon.ad_level);
    this.AdvFormService.publishData({
      coupon_redemption_id: attempt.redemptionId,
      coupon_request_id: attempt.requestId,
    }).pipe(finalize(() => { this.couponBusy = false; this.finishSubmission(); })).subscribe({
      next: () => {
        this.couponAttempt = null;
        this.modalService.open(freeAdv, {
          width: '460px', maxWidth: 'calc(100vw - 24px)', direction: 'rtl'
        });
      },
      error: error => {
        const status = Number(error?.status ?? 0);
        // Network / timeout / server error: keep the id so a retry is the same attempt.
        if (status > 0 && status < 500) this.couponAttempt = null;
        this.couponError = error?.error?.message || error?.error?.error
          || 'לא הצלחנו לפרסם את המודעה. בדקו את החיבור ונסו שוב.';
        if (this.couponService.isCouponRefusal(error)) {
          this.loadCoupons();
        }
      }
    });
  }

  ngOnInit(): void {
    this.http.get(this.apiUrl.getQuotaUrl(), this.options).subscribe({
      next: (q: any) => this.vipQuota = q,
      error: () => this.vipQuota = null,
    });
    // SCRUM-313: in parallel with the quota; the routes come from AdvFormService.
    this.loadCoupons();
    // SCRUM-239: the תקנון is approved once, not on every publish. If the
    // server already has this user's approval of the current version, the
    // plans are unlocked straight away with no prompt.
    this.consentService.getStatus().subscribe({
      next: status => {
        if (status?.acceptTerms) this.markTermsAccepted();
      },
      error: () => {}   // logged out / offline: the in-form approval still works
    });
  }

  /** Unlocks the plans for this publish session and ticks the form's box. */
  private markTermsAccepted(): void {
    this.advFormService.termsAcceptedLocally = true;
    const contact = this.advFormService.getQuestion('userContactDetails');
    if (contact) {
      contact.acceptTerms = true;
    } else {
      this.advFormService.addQuestion('userContactDetails', {acceptTerms: true});
    }
    if (this.submitError === this.termsRequiredMessage) this.submitError = '';
  }
  publishAdv():Observable<any>{
    return this.AdvFormService.publishData();
  }

  selectRoute(freeAdv: TemplateRef<unknown>, routeType: adsRoute): void {
    if (this.isSubmitting || this.isBlocked(routeType)) {
      return;
    }
    if (!this.termsAccepted) {
      this.submitError = this.termsRequiredMessage;
      return;
    }

    this.isSubmitting = true;
    this.submitError = '';
    this.selectedLevel = routeType.level;
    this.advFormService.addQuestion("adsRoute",routeType.level)
    const selectedRoute = (this.advFormService.adsRoutes || []).find(ads=>ads.level == routeType.level)
    if(routeType.level === RouteType.LEVEL1.toString()){
      this.publishAdv().pipe(finalize(() => this.finishSubmission())).subscribe({
        next: () => this.modalService.open(freeAdv, {
          width: '460px',
          maxWidth: 'calc(100vw - 24px)',
          direction: 'rtl'
        }),
        error: error => this.handleSubmitError(error)
      });
    }else if(routeType.level === RouteType.LEVEL2.toString()){
      this.openPaymentAfterPublish(selectedRoute, routeType);
    }else if(routeType.level === RouteType.LEVEL3.toString()){
      this.openPaymentAfterPublish(selectedRoute, routeType);
    } else {
      this.finishSubmission();
      this.submitError = 'לא ניתן לזהות את המסלול שנבחר. נסו שוב.';
    }
  }

  /** SCRUM-86 — publish without payment, within quota. */
  vipPublish(freeAdv: TemplateRef<unknown>): void {
    if (this.vipBusy || this.isSubmitting) return;
    if (!this.termsAccepted) {
      this.submitError = this.termsRequiredMessage;
      return;
    }
    this.vipBusy = true;
    this.isSubmitting = true;
    this.submitError = '';
    // Route is irrelevant for a VIP (the backend bypasses route rules
    // for them) but the payload needs one; LEVEL1 keeps analytics honest.
    this.advFormService.addQuestion('adsRoute', RouteType.LEVEL1);
    this.publishAdv().pipe(finalize(() => { this.vipBusy = false; this.finishSubmission(); })).subscribe({
      next: () => this.modalService.open(freeAdv, {
        width: '460px', maxWidth: 'calc(100vw - 24px)', direction: 'rtl'
      }),
      error: error => this.handleSubmitError(error)
    });
  }

  /** At the limit — send them to the plans page. */
  vipUpgrade(): void {
    window.location.href = '/subscriptions';
  }

  private openPaymentAfterPublish(selectedRoute: adsRoute | undefined, routeType: adsRoute): void {
    this.publishAdv().pipe(finalize(() => this.finishSubmission())).subscribe({
      next: res => this.modalService.open(CreditCardComponent, {
        data: {adsRoute: selectedRoute || routeType, adsId: res['id']},
        width: '860px',
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        direction: 'rtl',
        panelClass: 'sale4all-payment-dialog',
        autoFocus: 'first-tabbable'
      }),
      error: error => this.handleSubmitError(error)
    });
  }

  private handleSubmitError(error: any): void {
    this.submitError = error?.error?.message || error?.error?.error || 'לא הצלחנו להכין את המודעה. בדקו את החיבור ונסו שוב.';
  }

  private finishSubmission(): void {
    this.isSubmitting = false;
    this.loadingMaskServiceService.setOnLoadingMask(false, false);
  }

}
